import {
  memo,
  useEffect,
  useRef,
  useState,
  useCallback,
  useLayoutEffect,
  useMemo,
} from "react";
import "./GlobalChat.scss";
import "../../../shared/chatReply/chatReply.scss";
import { Spin, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Link } from "react-router-dom";
import { getLocalStorageData } from "../../../shared/commonFunction";
import { getApiRequest } from "../../../services/getApiRequest";
import { END_POINTS } from "../../../Helper/Constant";
import sendIcon from "../../../assets/icons/send-icon.svg";
import fire from "../../../assets/icons/fire.svg";
import useSocket from "../../../hooks/useSocket";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import ChatMessageText from "../../../shared/sharedComponents/ChatMessageText/ChatMessageText";
import ChatUserAvatar from "../../../shared/sharedComponents/ChatUserAvatar/ChatUserAvatar";
import { vipMessageClass, isUserVip } from "../../../shared/vip/isUserVip";
import VipCarousel from "../../../shared/sharedComponents/VipCarousel/VipCarousel";
import {
  ArrowLeftOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import VipJoinModal from "../../../shared/sharedComponents/VipJoinModal/VipJoinModal";
import vipStar from "../../../assets/images/star-icon.png";
import VipStar from "../../../shared/sharedComponents/VipStar/VipStar";
import { showToast } from "../../../shared/sharedComponents/ToasterMessage/ToasterMessage";
import {
  useChatReply,
  ChatReplyQuote,
  ChatReplyPreviewBar,
  mergeMessagesById,
} from "../../../shared/chatReply";
import {
  useAllowedEmojis,
  useChatReactions,
  ChatBubbleActions,
  patchMessageReactions,
  REACTION_CHAT_TYPES,
} from "../../../shared/chatReactions";
import {
  ChatComposerField,
  collectChatUsers,
  mentionedRowClass,
} from "../../../shared/chatMentions";

const INITIAL_MESSAGE_LIMIT = 20;
const LOAD_MORE_MESSAGE_LIMIT = 10;
const SCROLL_LOAD_THRESHOLD = 80;

const GlobalChat = ({ setChatIsVisible, isMobile }) => {
  const { t, i18n } = useTranslation("common");
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState({
    text: "",
    imageUrl: "",
    videoUrl: "",
  });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  const [todayDate] = useState(moment().format("D MMM"));
  const [joinVipOpen, setJoinVipOpen] = useState(false);
  const [vipListKey, setVipListKey] = useState(0);
  const [isCurrentUserVip, setIsCurrentUserVip] = useState(false);

  const chatContainerRef = useRef(null);
  const chatEndRef = useRef(null);
  const pageRef = useRef(1);
  const serverPaginatedRef = useRef(false);
  const allMessagesBufferRef = useRef([]);
  const scrollSnapshotRef = useRef(null);
  const shouldScrollToBottomRef = useRef(true);
  const isLoadingMoreRef = useRef(false);
  const isInitialLoadRef = useRef(true);

  const socketConnection = useSocket();
  const { socket, isReady } = socketConnection;

  const userData =
    useSelector((state) => state.user.userData) ||
    JSON.parse(getLocalStorageData("user"));
  const onlineUsers = useSelector((state) => state.user.onlineUser) || [];
  const isLogin = useSelector((state) => state.login.isLogin);
  const [directoryUsers, setDirectoryUsers] = useState([]);

  const {
    replyToTarget,
    replySenderName,
    highlightedMessageId,
    contextLoading,
    handleStartReply,
    handleCancelReply,
    clearReplyAfterSend,
    handleJumpToQuotedMessage,
    finishContextJump,
    getBubbleGestureHandlers,
    setMessageRef,
  } = useChatReply({
    messages,
    senderField: "msgByUserId",
    onFetchContext: (quoted) => {
      socket?.emit("global-message-context", {
        messageId: quoted._id,
        limit: INITIAL_MESSAGE_LIMIT,
      });
    },
  });

  const { allowedEmojis } = useAllowedEmojis();

  const applyReactions = useCallback((messageId, reactions) => {
    const patch = (list) =>
      patchMessageReactions(list || [], messageId, reactions);
    allMessagesBufferRef.current = patch(allMessagesBufferRef.current);
    setMessages(patch);
  }, []);

  const { pickerMessageId, togglePicker, handleReact } = useChatReactions({
    socket,
    chatType: REACTION_CHAT_TYPES.GLOBAL,
    currentUser: userData,
    allowedEmojis,
    onUpdate: applyReactions,
  });

  const mentionUsers = useMemo(
    () =>
      collectChatUsers({
        messages: [...(allMessagesBufferRef.current || []), ...messages],
        extraUsers: [
          ...(Array.isArray(onlineUsers) ? onlineUsers : []),
          ...directoryUsers,
        ],
        currentUserId: userData?._id,
      }),
    [messages, onlineUsers, directoryUsers, userData?._id]
  );

  const searchMentionUsers = useCallback(async (query) => {
    if (!query) return [];
    try {
      const response = await getApiRequest(
        `${END_POINTS.FRIENDS_SEARCH}?search=${encodeURIComponent(query)}`
      );
      return response?.users || response?.friends || [];
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    Promise.allSettled([
      getApiRequest(`${END_POINTS.FRIENDS_LIST}?page=1&limit=100`),
      getApiRequest(`${END_POINTS.VIP_LIST}?page=1&limit=50`),
    ]).then((results) => {
      if (!mounted) return;
      const extra = [];
      results.forEach((result) => {
        if (result.status !== "fulfilled") return;
        const data = result.value || {};
        extra.push(
          ...(data.friends || []),
          ...(data.users || []),
          ...(data.vips || []),
          ...(data.vipList || [])
        );
      });
      setDirectoryUsers(extra);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const handleGlobalMessageContext = useCallback(
    (payload) => {
      const targetId = payload?.messageId;

      if (!payload?.found) {
        showToast(
          t("one_to_one.message_unavailable") || "Message unavailable",
          "info"
        );
        finishContextJump(false);
        return;
      }

      const incoming = payload?.messages || [];
      shouldScrollToBottomRef.current = false;
      setMessages((prev) => mergeMessagesById(prev, incoming));

      if (payload?.pagination?.hasMore !== undefined) {
        setHasMoreOlder(Boolean(payload.pagination.hasMore));
      }

      finishContextJump(true, targetId);
    },
    [finishContextJump, t]
  );

  const applyClientSideWindow = useCallback((allMessages, shownCount) => {
    const total = allMessages.length;
    const visibleCount = Math.min(shownCount, total);
    const visibleMessages = allMessages.slice(total - visibleCount);

    setMessages(visibleMessages);
    setHasMoreOlder(total - visibleCount > 0);

    return visibleCount;
  }, []);

  const handleGlobalMessage = useCallback(
    (data) => {
      const incoming = Array.isArray(data)
        ? data
        : data?.messages || [];
      console.log('incoming: ', incoming);
      const isLoadingOlder = isLoadingMoreRef.current;
      const isInitialLoad = isInitialLoadRef.current;

      setLoading(false);
      setLoadingMore(false);

      const serverHasPagination =
        data?.hasMore !== undefined || data?.pagination !== undefined;

      if (serverHasPagination) {
        serverPaginatedRef.current = true;
        const hasMore = data?.hasMore ?? data?.pagination?.hasMore ?? false;

        if (isLoadingOlder) {
          setMessages((prev) => {
            const existingIds = new Set(prev.map((msg) => msg._id));
            const olderMessages = incoming.filter(
              (msg) => !existingIds.has(msg._id)
            );
            return [...olderMessages, ...prev];
          });
          setHasMoreOlder(hasMore);
        } else {
          setMessages(incoming);
          setHasMoreOlder(hasMore);
          shouldScrollToBottomRef.current = true;
        }
      } else if (isInitialLoad) {
        allMessagesBufferRef.current = incoming;
        applyClientSideWindow(incoming, INITIAL_MESSAGE_LIMIT);
        shouldScrollToBottomRef.current = true;
      } else {
        allMessagesBufferRef.current = incoming;

        setMessages((prev) => {
          const prevIds = new Set(prev.map((msg) => msg._id));
          const newMessages = incoming.filter((msg) => !prevIds.has(msg._id));

          if (newMessages.length === 0) {
            const visibleCount = Math.min(prev.length, incoming.length);
            const nextMessages = incoming.slice(
              incoming.length - visibleCount
            );
            setHasMoreOlder(incoming.length - visibleCount > 0);
            return nextMessages;
          }

          const container = chatContainerRef.current;
          const wasNearBottom =
            container &&
            container.scrollHeight -
            container.scrollTop -
            container.clientHeight <
            120;

          if (wasNearBottom) {
            shouldScrollToBottomRef.current = true;
            const visibleCount = Math.min(
              prev.length + newMessages.length,
              incoming.length
            );
            const nextMessages = incoming.slice(
              incoming.length - visibleCount
            );
            setHasMoreOlder(incoming.length - visibleCount > 0);
            return nextMessages;
          }

          setHasMoreOlder(incoming.length - prev.length > 0);
          return prev;
        });
      }

      isLoadingMoreRef.current = false;
      isInitialLoadRef.current = false;
    },
    [applyClientSideWindow]
  );

  const fetchMessages = useCallback(
    (page = 1, limit = INITIAL_MESSAGE_LIMIT, before = null) => {
      if (!socket) return;

      socket.emit("global-message-page", {
        page,
        limit,
        ...(before ? { before } : {}),
      });
    },
    [socket]
  );

  const loadOlderMessages = useCallback(() => {
    if (
      loadingMore ||
      isLoadingMoreRef.current ||
      !hasMoreOlder ||
      loading
    ) {
      return;
    }

    const container = chatContainerRef.current;
    if (container) {
      scrollSnapshotRef.current = {
        scrollHeight: container.scrollHeight,
        scrollTop: container.scrollTop,
      };
    }

    setLoadingMore(true);
    isLoadingMoreRef.current = true;
    shouldScrollToBottomRef.current = false;

    if (serverPaginatedRef.current) {
      pageRef.current += 1;
      const oldestMessage = messages[0];
      fetchMessages(
        pageRef.current,
        LOAD_MORE_MESSAGE_LIMIT,
        oldestMessage?._id
      );
      return;
    }

    setMessages((prev) => {
      const nextCount = Math.min(
        prev.length + LOAD_MORE_MESSAGE_LIMIT,
        allMessagesBufferRef.current.length
      );
      const nextMessages = allMessagesBufferRef.current.slice(
        allMessagesBufferRef.current.length - nextCount
      );
      setHasMoreOlder(nextCount < allMessagesBufferRef.current.length);
      return nextMessages;
    });

    window.setTimeout(() => {
      setLoadingMore(false);
      isLoadingMoreRef.current = false;
    }, 300);
  }, [fetchMessages, hasMoreOlder, loading, loadingMore, messages]);

  useEffect(() => {
    if (!socket) return;

    const requestMessages = () => {
      if (!socket.connected) return;
      isInitialLoadRef.current = true;
      pageRef.current = 1;
      serverPaginatedRef.current = false;
      socket.emit("global-message-page", {
        page: 1,
        limit: INITIAL_MESSAGE_LIMIT,
      });
    };

    socket.on("globalMessages", handleGlobalMessage);
    socket.on("globalMessageContext", handleGlobalMessageContext);
    socket.on("connect", requestMessages);

    if (socket.connected) {
      requestMessages();
    }

    const retryTimer = window.setTimeout(() => {
      if (isInitialLoadRef.current) {
        requestMessages();
      }
    }, 1500);

    return () => {
      window.clearTimeout(retryTimer);
      socket.off("globalMessages", handleGlobalMessage);
      socket.off("globalMessageContext", handleGlobalMessageContext);
      socket.off("connect", requestMessages);
    };
  }, [socket, isReady, handleGlobalMessage, handleGlobalMessageContext]);

  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (container.scrollTop <= SCROLL_LOAD_THRESHOLD) {
        loadOlderMessages();
      }
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [loadOlderMessages]);

  useLayoutEffect(() => {
    const snapshot = scrollSnapshotRef.current;
    const container = chatContainerRef.current;

    if (snapshot && container) {
      const heightDiff = container.scrollHeight - snapshot.scrollHeight;
      container.scrollTop = snapshot.scrollTop + heightDiff;
      scrollSnapshotRef.current = null;
      return;
    }

    if (shouldScrollToBottomRef.current) {
      chatEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
      shouldScrollToBottomRef.current = false;
    }
  }, [messages]);

  const handleSendMessage = useCallback(
    (e) => {
      e.preventDefault();
      if (!message.text && !message.imageUrl && !message.videoUrl) return;
      if (!socket) return;

      const payload = {
        text: message.text,
        imageUrl: message.imageUrl,
        videoUrl: message.videoUrl,
        msgByUserId: userData?._id,
      };

      if (replyToTarget?._id) {
        payload.replyTo = replyToTarget._id;
      }

      socket.emit("new-global-message", payload);

      setMessage({ text: "", imageUrl: "", videoUrl: "" });
      clearReplyAfterSend();
    },
    [message, replyToTarget, socket, userData?._id, clearReplyAfterSend]
  );

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage(e);
      }
    },
    [handleSendMessage]
  );

  const renderDate = useCallback(
    (msg, index) => {
      if (
        index === 0 ||
        moment(messages[index - 1]?.createdAt).format("LL") !==
        moment(msg?.createdAt).format("LL")
      ) {
        return (
          <>
            <div className="msg-divide-hr"></div>
            <div key={`date-${index}`} className="message-date">
              {moment(msg?.createdAt).format("D MMM") === todayDate
                ? "Today"
                : moment(msg?.createdAt).format("D MMM, h:mmA")}
            </div>
          </>
        );
      }
      return null;
    },
    [messages, todayDate]
  );

  const renderSenderName = (msg, isMine) => {
    return (
      <Link
        to={
          msg.userReference === "User" && msg?.msgByUserId?._id
            ? `/consumer-Profile/${msg.msgByUserId._id}`
            : undefined
        }
        className="global-chat-sender-link"
      >
        <p className={`message-inner-user-name${isMine ? " is-self" : ""}`}>
          {isUserVip(msg?.msgByUserId) && <VipStar />}
          {msg?.msgByUserId?.userName}
          {msg?.msgByUserId?.winStreak > 0 && (
            <>
              <Tooltip title={t("one_to_one.win_streak")}>
                <img
                  src={fire}
                  alt="fire"
                  className="chat-fire-img"
                  loading="lazy"
                />
              </Tooltip>
              <span>{msg?.msgByUserId?.winStreak}</span>
            </>
          )}
        </p>
      </Link>
    );
  };

  return (
    <div className="global-chat-box">
      <div className="chat-header vip-chat-header">
        <div className="chat-header-left">
          {!isMobile && isLogin && (
            <ArrowLeftOutlined
              onClick={() => setChatIsVisible(false)}
              className="chat-back-icon"
            />
          )}
          <span className="chat-header-title">{t("one_to_one.global_chat")}</span>
        </div>
        <div className="chat-header-right">
          {isCurrentUserVip ? (
            <span className="vip-header-badge">
              <img src={vipStar} alt="" className="vip-join-cta__star" />
              <span>{t("vip.header_label")}</span>
            </span>
          ) : (
            <button
              type="button"
              className={`vip-join-cta${i18n.language === "ar" ? " vip-join-cta--ar" : ""}`}
              onClick={() => setJoinVipOpen(true)}
            >
              <img src={vipStar} alt="" className="vip-join-cta__star" />
              <span dir={i18n.language === "ar" ? "rtl" : "ltr"}>{t("vip.join_cta")}</span>
            </button>
          )}
        </div>
      </div>

      <VipCarousel
        refreshKey={vipListKey}
        onCurrentUserVipChange={setIsCurrentUserVip}
      />

      <VipJoinModal
        open={joinVipOpen}
        onClose={() => setJoinVipOpen(false)}
        onJoined={() => {
          setIsCurrentUserVip(true);
          setVipListKey((prev) => prev + 1);
        }}
      />

      <div className="global-message-container" ref={chatContainerRef}>
        {loading && (
          <Spin
            indicator={<LoadingOutlined spin />}
            className="global-spin"
            size="large"
          />
        )}

        {loadingMore && (
          <div className="global-chat-load-more">
            <Spin indicator={<LoadingOutlined spin />} size="small" />
          </div>
        )}

        {messages?.map((msg, index) => (
          <div
            key={msg._id || `msg-${index}`}
            ref={(el) => setMessageRef(msg._id, el)}
            data-message-id={msg._id}
            className={`chat-message-row${highlightedMessageId === msg._id ? " is-highlighted" : ""}${mentionedRowClass(msg, userData?._id)}`}
          >
            {msg.messageType === "chat" ? (
              <div className="particular-user-chat">
                {renderDate(msg, index)}
                <div
                  className={`global-message-bubble ${userData._id === msg?.msgByUserId?._id ? "received" : "sent"
                    }${vipMessageClass(msg, msg?.msgByUserId, userData)}`}
                  {...getBubbleGestureHandlers(msg)}
                >
                  <div className="one-to-one-message global-chat-msg">
                    <ChatUserAvatar
                      user={msg?.msgByUserId}
                      className="chat-framed-avatar--gutter"
                      to={
                        msg.userReference === "User" && msg?.msgByUserId?._id
                          ? `/consumer-Profile/${msg.msgByUserId._id}`
                          : undefined
                      }
                    />

                    <div className="global-chat-msg__body">
                      {(msg.text || msg.imageUrl || msg.videoUrl || msg.replyTo) && (
                        <>
                          {renderSenderName(
                            msg,
                            userData?._id === msg?.msgByUserId?._id
                          )}
                          <div
                            className={
                              userData?._id !== msg?.msgByUserId?._id
                                ? "message-text-sent"
                                : "message-text-received"
                            }
                          >

                            {msg.replyTo && (
                              <ChatReplyQuote
                                replyTo={msg.replyTo}
                                senderField="msgByUserId"
                                onJump={handleJumpToQuotedMessage}
                              />
                            )}
                            {msg.text && <ChatMessageText text={msg.text} users={mentionUsers} />}
                          </div>

                          <ChatBubbleActions
                            message={msg}
                            currentUserId={userData?._id}
                            allowedEmojis={allowedEmojis}
                            pickerOpen={pickerMessageId === msg._id}
                            onTogglePicker={togglePicker}
                            onReact={handleReact}
                            onReply={handleStartReply}
                          />
                        </>
                      )}

                      {(msg.text || msg.imageUrl || msg.videoUrl || msg.replyTo) && (
                        <p className="message-timestamp">
                          {moment(msg.createdAt).format("h:mmA")}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {renderDate(msg, index)}
                <div className="invite-message">
                  <ChatMessageText text={msg.text} users={mentionUsers} />
                </div>
              </>
            )}
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>

      <div className="global-type-message-section">
        {replyToTarget && (
          <ChatReplyPreviewBar
            replyToTarget={replyToTarget}
            senderName={replySenderName}
            onCancel={handleCancelReply}
          />
        )}
        <div className="global-input-send">
          <div className="global-chat-input">
            <ChatComposerField
              name="text"
              id="text"
              value={message.text}
              onChange={(text) =>
                setMessage((prev) => ({ ...prev, text }))
              }
              onKeyDown={handleKeyPress}
              mentionUsers={mentionUsers}
              searchUsers={searchMentionUsers}
              currentUserId={userData?._id}
              className="global-msg-input"
              placeholder={t("chatMessages.placeholder")}
            />
          </div>
          <div className="global-send-btn">
            <img
              src={sendIcon}
              alt="loading-select-file"
              className="global-send-icon-image"
              onClick={handleSendMessage}
              loading="lazy"
            />
          </div>
        </div>
        {contextLoading && (
          <div className="global-reply-context-loading">
            <Spin indicator={<LoadingOutlined spin />} size="small" />
          </div>
        )}
      </div>
    </div>
  );
};

GlobalChat.propTypes = {
  setChatIsVisible: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
};

export default memo(GlobalChat);
